/**
 * IPCC AR6 WGI SPM Comprehensive Datasets (Figures SPM.1 to SPM.10)
 * & SR15 SPM Figures (SR15.1 to SR15.4)
 * Source: IPCC Sixth Assessment Report (WGI) & Special Report on 1.5°C (SR15)
 */

export interface SPMFigureMeta {
  id: string;
  number: string;
  title: string;
  subtitle: string;
  description: string;
  keyTakeaways: string[];
  policyRelevance: string;
  originalFigureLayout: string;
  originalIpccUrl: string;
  acousticMapping: {
    pitch: string;
    timbre: string;
    pan: string;
    intensity: string;
  };
}

export const SPM_FIGURE_METADATA: Record<string, SPMFigureMeta> = {  'spm1': {
    id: 'spm1',
    number: 'SPM.1',
    title: 'History of Global Surface Temperature',
    subtitle: 'Changes in global surface temperature over 2,000 years and modern human vs natural forcing',
    description: 'Human influence has warmed the climate at a rate that is unprecedented in at least the last 2000 years. The panel shows multi-century paleo temperature reconstruction and modern (1850–2020) observed warming vs simulated human + natural forcing duet.',
    keyTakeaways: [
      'Global surface temperature in 2011–2020 was 1.09°C higher than in 1850–1900.',
      'The scale of recent climate changes across the system is unprecedented over centuries to thousands of years.',
      'Human influence is the main driver of global warming since 1950, with natural forcings (solar/volcanoes) contributing near zero net warming.'
    ],
    policyRelevance: 'Establishes unequivocal human responsibility for global warming, serving as the foundational physical science basis for international climate negotiations.',
    originalFigureLayout: 'Dual-panel layout: Panel (a) shows multi-century reconstructed global temperature over 2000 years (line + gray uncertainty band). Panel (b) zooms into 1850–2020 comparing observed warming against CMIP6 climate model simulations for human + natural drivers vs natural drivers alone.',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-1',
    acousticMapping: {
      pitch: 'Temperature anomaly (°C): 0.0°C = C4 (261.63 Hz). Rising temperature increases pitch.',
      timbre: 'Nat-Warmth (Subtractive dual-triangle) for natural drivers; Anthropo-Pulse (Bright FM synth) for human drivers.',
      pan: 'Natural forcing panned center-left (-0.3); Human forcing panned center-right (+0.3).',
      intensity: 'Volume & harmonic brightness scale with absolute forcing magnitude.'
    }
  },
  'spm2': {
    id: 'spm2',
    number: 'SPM.2',
    title: 'Drivers of Recent Climate Warming',
    subtitle: 'Assessed radiative forcing and temperature contribution by driver in 2019 relative to 1750',
    description: 'Observed warming is driven by emissions from human activities, with greenhouse gas warming partly masked by aerosol cooling. Clicking or playing drivers triggers their individual sonified acoustic identity.',
    keyTakeaways: [
      'Well-mixed greenhouse gases (CO2, CH4, N2O, halocarbons) contributed +1.5°C to +1.8°C of warming.',
      'Cooling from anthropogenic aerosols (SO2, organic carbon) masked roughly 0.4°C to 0.7°C of greenhouse warming.',
      'Methane (CH4) is the second largest driver after CO2, accounting for ~0.5°C of warming.'
    ],
    policyRelevance: 'Highlights the critical importance of rapid methane reduction alongside carbon dioxide decarbonization to slow near-term warming.',
    originalFigureLayout: 'Bar chart panel displaying assessed radiative forcing (W/m²) and temperature contributions (°C) in 2019 broken down by chemical species (CO2, CH4, N2O, Halocarbons, NOx, VOC, SO2, Organic Carbon, Black Carbon, Land Use, Natural).',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-2',
    acousticMapping: {
      pitch: 'Warming drivers yield high pitch (+°C); Cooling drivers (aerosols) yield inverted low-pitch tone.',
      timbre: 'CO2/CH4 (FM Pulse), SO2/Organic Aerosols (Filtered Pink Noise), Land Use (Subtractive tone).',
      pan: 'Warming drivers panned Right; Cooling drivers panned Left.',
      intensity: 'Bar height directly maps to synthesis amplitude.'
    }
  },
  'spm3': {
    id: 'spm3',
    number: 'SPM.3',
    title: 'Regional Climate Extremes Hex-Grid',
    subtitle: 'Synthesis of observed changes in hot extremes, heavy precipitation, and drought across 45 global regions',
    description: 'Climate change is already affecting every inhabited region across the globe. Hover or scan regions to hear spatial percussive transients whose pitch reflects confidence and warming intensity.',
    keyTakeaways: [
      'Human-induced climate change is already affecting weather and climate extremes in every region across the globe.',
      'Evidence of observed changes in extremes such as heatwaves, heavy precipitation, droughts, and tropical cyclones has strengthened since AR5.',
      'High confidence of human attribution across 41 out of 45 global land regions for hot extremes.'
    ],
    policyRelevance: 'Demonstrates that loss, damage, and extreme weather impacts are already occurring globally rather than being distant future projections.',
    originalFigureLayout: 'Hexagonal grid map of 45 WGI reference land regions colored by change direction (increase/decrease) and confidence dots (high/medium/low) across three separate panels: (a) Hot Extremes, (b) Heavy Precipitation, and (c) Agricultural & Ecological Drought.',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-3',
    acousticMapping: {
      pitch: 'Confidence level (High/Medium/Low) and increase severity map to pitch height.',
      timbre: 'Extreme-Transient FM generator triggering percussive hit per region.',
      pan: 'Geographic Longitude scanning Left-to-Right (-1.0 to +1.0 pan).',
      intensity: 'Human contribution confidence scales hit velocity.'
    }
  },
  'spm4': {
    id: 'spm4',
    number: 'SPM.4',
    title: 'Future Climate Scenarios & Impact Curves',
    subtitle: 'Projections for temperature, September Arctic sea ice extent, and ocean surface pH (2015–2100)',
    description: 'Five illustrative emissions scenarios (SSP1-1.9 to SSP5-8.5). As temperature diverges upwards, Arctic sea ice noise decays and ocean acidification causes audio bit-crushing.',
    keyTakeaways: [
      'Global surface temperature will continue to increase until at least mid-century under all emissions scenarios considered.',
      'Global warming of 1.5°C and 2°C will be exceeded during the 21st century unless deep reductions in CO2 and other greenhouse gas emissions occur in the coming decades.',
      'The Arctic is projected to be practically sea ice-free in September at least once before 2050 under all scenarios.'
    ],
    policyRelevance: 'Shows that staying below 1.5°C requires immediate, deep, and sustained emissions cuts (SSP1-1.9 pathway).',
    originalFigureLayout: 'Multi-panel trajectory curves over 2015–2100 for 5 SSP scenarios: Panel (a) Global surface temperature, Panel (b) September Arctic sea ice area, Panel (c) Global ocean surface pH, and Panel (d) Global mean sea level rise.',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-4',
    acousticMapping: {
      pitch: 'Polyphonic pitch divergence across active SSP scenario trajectories.',
      timbre: 'Cryo-Hiss (High-pass granular noise) decays as Arctic sea ice shrinks; Bit-crushing increases as pH drops.',
      pan: 'Scenarios spread across stereo field (SSP1 left -> SSP5 right).',
      intensity: 'High emissions (SSP5-8.5) generate harsh FM modulation index.'
    }
  },
  'spm5': {
    id: 'spm5',
    number: 'SPM.5',
    title: 'Spatial Patterns of Warming & Precipitation',
    subtitle: 'Latitude scanning comparison of global warming patterns at +1.5°C, +2.0°C, and +4.0°C',
    description: 'Every region will experience distinct spatial warming patterns, with polar amplification causing northern high latitudes to warm at twice the global rate.',
    keyTakeaways: [
      'Warming is greater over land than over oceans, and polar amplification causes the Arctic to warm at more than twice the global rate.',
      'Precipitation is projected to increase in high latitudes, the equatorial Pacific and parts of the monsoon regions, but decrease over parts of the subtropics.',
      'Every additional fraction of a degree of warming increases regional temperature and precipitation extremes.'
    ],
    policyRelevance: 'Informs regional climate adaptation planning by illustrating localized warming and precipitation changes at different global warming levels.',
    originalFigureLayout: 'Global map comparison across 3 warming levels (+1.5°C, +2.0°C, +4.0°C) showing: (a) Annual mean temperature change (°C), (b) Annual mean precipitation change (%), and (c) Annual mean soil moisture change.',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-5',
    acousticMapping: {
      pitch: 'South-to-North latitude sweep. Pitch spikes violently over the Arctic (+90°N polar amplification).',
      timbre: 'Dual FM carrier + bandpass noise simulating precipitation anomalies.',
      pan: 'Smooth South-to-North acoustic sweep.',
      intensity: 'Global warming level (+1.5°C vs +4.0°C) boosts overall harmonic density.'
    }
  },
  'spm6': {
    id: 'spm6',
    number: 'SPM.6',
    title: 'Cumulative CO2 Emissions & Temp Response',
    subtitle: 'Linear relationship between cumulative CO2 emissions and global surface temperature increase',
    description: 'Near-linear relationship between cumulative anthropogenic CO2 emissions and global surface warming. Each 1000 GtCO2 causes ~0.45°C of warming (TCRE).',
    keyTakeaways: [
      'Each 1000 GtCO2 of cumulative CO2 emissions is assessed to cause a likely 0.27°C to 0.63°C increase in global surface temperature (best estimate 0.45°C).',
      'Limiting human-induced global warming to a specific level requires limiting cumulative CO2 emissions—reaching at least net zero CO2 emissions.',
      'From 1850 to 2019, humanity emitted approximately 2390 GtCO2.'
    ],
    policyRelevance: 'Establishes the scientific requirement for net zero carbon emissions to stabilize global temperatures.',
    originalFigureLayout: 'Scatter/line plot showing cumulative CO2 emissions from 1850 (GtCO2) on the X-axis against global surface temperature change (°C) on the Y-axis, with historical emissions band and projected scenario fan lines.',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-6',
    acousticMapping: {
      pitch: 'Linear pitch rise as cumulative emissions increase from 0 to 4500 GtCO2.',
      timbre: 'Carbon Metronome beat (100 GtCO2 pulse hits) with stereo detune for model uncertainty bounds.',
      pan: 'Uncertainty range detune (Lower bound left, Upper bound right).',
      intensity: 'Pulse rate and metronome gain increase with cumulative total.'
    }
  },
  'spm7': {
    id: 'spm7',
    number: 'SPM.7',
    title: 'Extreme Weather Event Frequencies',
    subtitle: 'Frequency acceleration of 10-year and 50-year extreme heat, precipitation, and drought events',
    description: 'Events that occurred once every 10 or 50 years in a pre-industrial climate become vastly more frequent and intense at +1.5°C, +2°C, and +4°C.',
    keyTakeaways: [
      'A 50-year extreme heat event in a pre-industrial climate occurs 8.6 times more often at 1.5°C and 39.2 times more often at 4°C.',
      'Heavy precipitation events that occurred once per decade become 1.5x more frequent at 1.5°C and 2.7x more frequent at 4°C.',
      'Agricultural and ecological droughts in drying regions become 2.0x more frequent at 1.5°C and 4.1x more frequent at 4°C.'
    ],
    policyRelevance: 'Illustrates why even fractions of a degree matter immensely for extreme weather risk and disaster response infrastructure.',
    originalFigureLayout: 'Infographic panels comparing event frequency (dots per 10/50 years) and intensity increase across 4 warming levels (Pre-industrial, 1.5°C, 2°C, 4°C) for Extreme Heat, Heavy Rain, and Agricultural Drought.',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-7',
    acousticMapping: {
      pitch: 'Event intensity (+°C or +% moisture anomaly) maps to pulse pitch.',
      timbre: 'Extreme-Transient percussive metronome accelerating from 1x up to 39x speed.',
      pan: '10-year events (Left channel) vs 50-year events (Right channel).',
      intensity: 'Event frequency multiplier directly speeds up rhythmic pulse density.'
    }
  },
  'spm8': {
    id: 'spm8',
    number: 'SPM.8',
    title: 'Multi-Century Sea Level Rise Commitment',
    subtitle: 'Global mean sea level rise relative to 1900 projected to year 2300 and multi-millennial commitment',
    description: 'Sea level rise is irreversible over centuries to millennia. Low-likelihood high-impact ice sheet instability could drive sea level rise exceeding 5m by 2150.',
    keyTakeaways: [
      'Global mean sea level will continue to rise over the 21st century under all scenarios.',
      'By 2100, sea level rise is projected at 0.38–0.77m under SSP2-4.5 and up to 1.01m under SSP5-8.5.',
      'Low-likelihood, high-impact outcomes including ice sheet instability processes cannot be ruled out and could lead to sea level rise approaching 2m by 2100 and 5m by 2150 under SSP5-8.5.'
    ],
    policyRelevance: 'Forces coastal planners and infrastructure builders to plan for multi-century sea level rise commitment.',
    originalFigureLayout: 'Time series plot showing global mean sea level rise (meters) relative to 1900 from 2000 to 2100, extended to 2300 with dashed lines for low-likelihood high-impact ice sheet instability scenarios.',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-8',
    acousticMapping: {
      pitch: 'Abyssal-Drone sub-bass frequency rising smoothly from 20 Hz to 80 Hz.',
      timbre: 'Sub-harmonic sine drone with deep low-pass resonance simulating oceanic pressure.',
      pan: 'Deep center drone with sub-bass stereo widening.',
      intensity: 'Sea level magnitude (meters) increases sub-bass amplitude and sub-harmonic distortion.'
    }
  },
  'spm9': {
    id: 'spm9',
    number: 'SPM.9',
    title: 'Regional Climate Impact-Driver (CID) Matrix',
    subtitle: 'Grid of 33 physical climate impact-drivers projected to change across global sub-regions',
    description: 'Interactive matrix covering 33 CIDs across Heat & Cold, Wet & Dry, Wind, Snow & Ice, Coastal, and Open Ocean categories. Click cells to trigger synthesized acoustic textures.',
    keyTakeaways: [
      'All regions are projected to experience further increases in hot climate impact-drivers (CIDs) and decreases in cold CIDs.',
      'Coastal CIDs such as relative sea level rise, coastal flooding, and coastal erosion are projected to increase along almost all coasts.',
      'Multiple CIDs will change simultaneously across all global regions by mid-century.'
    ],
    policyRelevance: 'Provides a comprehensive risk matrix for multi-hazard regional climate risk assessments.',
    originalFigureLayout: 'Matrix grid displaying 33 physical CIDs (rows) against global land and ocean regions (columns) with color indicators for direction of change and confidence.',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-9',
    acousticMapping: {
      pitch: 'Category base frequency (Heat = high, Snow/Ice = high hiss, Coastal/Ocean = low sub).',
      timbre: '6 dedicated synthesis textures per CID category (Percussive, Noise, Sub, Granular).',
      pan: 'Matrix X-position maps to stereo field.',
      intensity: 'Increase confidence (High/Med/Low) scales texture envelope decay.'
    }
  },
  'spm10': {
    id: 'spm10',
    number: 'SPM.10',
    title: 'Remaining Carbon Budget Countdown',
    subtitle: 'Historical carbon emissions consumption vs remaining carbon allowances for 1.5°C and 2.0°C',
    description: 'To limit warming to 1.5°C with 50% probability, the remaining budget from 2020 is only ~500 GtCO2. Timeline scrubbing strips away bright harmonics as the allowance is consumed.',
    keyTakeaways: [
      'Remaining carbon budget from 2020 to limit warming to 1.5°C (50% chance) is approximately 500 GtCO2.',
      'To limit warming to 2.0°C (50% chance), the remaining budget is approximately 1150 GtCO2.',
      'At current annual emission rates (~40 GtCO2/yr), the 1.5°C budget will be exhausted in roughly one decade without rapid reductions.'
    ],
    policyRelevance: 'Defines the exact remaining carbon runway available before the 1.5°C Paris Agreement limit is breached.',
    originalFigureLayout: 'Stacked horizontal bar chart comparing historical cumulative CO2 emissions (1850–2019) against remaining CO2 budgets for 1.5°C and 2.0°C at various probability levels (17%, 33%, 50%, 67%, 83%).',
    originalIpccUrl: 'https://www.ipcc.ch/report/ar6/wg1/figures/summary-for-policymakers/figure-spm-10',
    acousticMapping: {
      pitch: 'Harmonic series frequency filtering.',
      timbre: 'High-frequency harmonics are stripped away by a dynamic low-pass ladder filter as allowance is consumed.',
      pan: 'Stereo width narrows down to mono as budget approaches 0 GtCO2.',
      intensity: 'Budget remaining percentage scales master brightness and resonance.'
    }
  },

  // --- IPCC SR15 FIGURES ---
  'sr15_spm1': {
    id: 'sr15_spm1',
    number: 'SR15 SPM.1',
    title: 'Human-Induced Warming & 1.5°C Trajectory',
    subtitle: 'Cumulative historical warming (1850–2017) and forced linear projection reaching 1.5°C by ~2040',
    description: 'Human-induced warming reached approximately 1.0°C above pre-industrial levels in 2017. At the current warming rate, global warming is projected to reach 1.5°C between 2030 and 2052.',
    keyTakeaways: [
      'Warming greater than the global average is being experienced in many regions, including 2x to 3x higher in the Arctic.',
      'Human-induced warming reached ~1.0°C (likely between 0.8°C and 1.2°C) in 2017.',
      'Warming from anthropogenic emissions from the pre-industrial period to the present will persist for centuries to millennia.'
    ],
    policyRelevance: 'Demonstrates the urgency of emission reductions in the 2020–2030 window to avoid crossing 1.5°C before 2040.',
    originalFigureLayout: 'Time series chart showing monthly global mean surface temperature (GMST) anomaly from 1850 to 2017 with human-induced warming trend line extending linearly to 1.5°C in ~2040.',
    originalIpccUrl: 'https://www.ipcc.ch/sr15/spm/spm-1/',
    acousticMapping: {
      pitch: 'Historical GMST pitch tracking. Crossing +1.5°C triggers a warning chime at 392 Hz (G4).',
      timbre: 'Nat-Warmth dual-triangle + Anthropo-Pulse FM synth duet.',
      pan: 'Historical trend center-left (-0.3); Projection trend center-right (+0.3).',
      intensity: 'Warning chime velocity scales as timeline approaches 1.5°C threshold.'
    }
  },
  'sr15_spm2': {
    id: 'sr15_spm2',
    number: 'SR15 SPM.2',
    title: '1.5°C Emission Pathways & CDR Portfolios',
    subtitle: 'Global net CO2 emissions and non-CO2 radiative forcing across P1, P2, P3, and P4 archetype pathways',
    description: 'Four illustrative archetype pathways (P1 to P4). P1 relies on low energy demand without BECCS; P4 relies on high energy demand and massive carbon dioxide removal (CDR).',
    keyTakeaways: [
      'In 1.5°C pathways with no or limited overshoot, global net anthropogenic CO2 emissions decline by about 45% from 2010 levels by 2030, reaching net zero around 2050.',
      'Pathways limiting warming to 1.5°C require rapid transitions in energy, land, urban, and industrial systems.',
      'All pathways that limit global warming to 1.5°C with limited or no overshoot use CDR, but the required scale varies from 100 GtCO2 (P1) to 1218 GtCO2 (P4).'
    ],
    policyRelevance: 'Informs national mitigation strategies by contrasting energy demand reduction vs heavy bioenergy carbon capture (BECCS) dependence.',
    originalFigureLayout: 'Four panel sub-graphs showing net global CO2 emissions trajectories (GtCO2/yr) from 2010 to 2100 for P1, P2, P3, and P4 archetype pathways with shaded areas for CDR/BECCS negative emissions.',
    originalIpccUrl: 'https://www.ipcc.ch/sr15/spm/spm-2/',
    acousticMapping: {
      pitch: 'Net CO2 trajectory pitch (higher emissions = higher pitch). Net zero = silence.',
      timbre: 'CDR / BECCS negative emissions render inverted sub-bass sine sweeps (Abyssal-Drone).',
      pan: 'CO2 emissions (Right channel) vs CDR carbon withdrawal (Left channel).',
      intensity: 'Pathway overshoot severity boosts FM index.'
    }
  },
  'sr15_spm3': {
    id: 'sr15_spm3',
    number: 'SR15 SPM.3',
    title: 'Impacts & Avoided Risks (1.5°C vs 2.0°C)',
    subtitle: 'Burning embers risk diagrams across Reasons for Concern (RFCs) and natural/human systems',
    description: 'Climate-related risks for natural and human systems are significantly lower at 1.5°C than at 2.0°C. Drag the warming slider from +0.5°C to +2.5°C to audition risk color transitions.',
    keyTakeaways: [
      'Limiting warming to 1.5°C compared to 2°C avoids severe impacts on ecosystems, human health, water availability, and food security.',
      'Warm-water coral reefs are projected to decline by 70–90% at 1.5°C, but >99% at 2.0°C.',
      'An ice-free Arctic summer is projected once per century at 1.5°C compared to once per decade at 2.0°C.'
    ],
    policyRelevance: 'Provides the core scientific justification for the 1.5°C temperature target of the Paris Agreement.',
    originalFigureLayout: 'Vertical "burning embers" bar diagrams showing risk level transitions (White -> Yellow -> Red -> Purple) across global temperature anomaly levels (0°C to 2.5°C) for 5 RFCs and specific systems.',
    originalIpccUrl: 'https://www.ipcc.ch/sr15/spm/spm-3/',
    acousticMapping: {
      pitch: 'Global temperature slider (+0.5°C to +2.5°C).',
      timbre: 'White (steady sine) -> Yellow (LFO vibrato) -> Red (Extreme-Transient dissonance) -> Purple (Cryo-Hiss saturation & collapse).',
      pan: 'System category panned across stereo field.',
      intensity: 'Risk color severity controls harmonic saturation and sub-bass collapse.'
    }
  },
  'sr15_spm4': {
    id: 'sr15_spm4',
    number: 'SR15 SPM.4',
    title: 'Mitigation Synergies & Trade-offs with SDGs',
    subtitle: 'Interactions between 1.5°C mitigation options and the 17 UN Sustainable Development Goals',
    description: '1.5°C mitigation options show robust synergies with Sustainable Development Goals, particularly for energy demand and land protection.',
    keyTakeaways: [
      'Mitigation options aligned with 1.5°C pathways show robust synergies with SDGs 3 (Health), 7 (Clean Energy), 12 (Consumption), and 13 (Climate Action).',
      'Energy demand reduction options show high synergies and low trade-offs across almost all SDGs.',
      'Land-intensive mitigation (large-scale BECCS) shows potential trade-offs with SDG 2 (Zero Hunger) and SDG 6 (Clean Water) if not managed sustainably.'
    ],
    policyRelevance: 'Aligns climate action directly with the UN 2030 Agenda for Sustainable Development.',
    originalFigureLayout: 'Interaction matrix table mapping mitigation option sectors (Energy Supply, Energy Demand, Land/Ocean) against the 17 SDGs with positive (synergy) and negative (trade-off) indicator bars.',
    originalIpccUrl: 'https://www.ipcc.ch/sr15/spm/spm-4/',
    acousticMapping: {
      pitch: 'Synergies play ascending major intervals (+3rd, +5th); Trade-offs play descending minor/tritone intervals.',
      timbre: 'Consonant sine tones for synergies; Dissonant sawtooth tones for trade-offs.',
      pan: 'SDG number maps to stereo field (SDG 1 left -> SDG 17 right).',
      intensity: 'Synergy/trade-off magnitude scales interval velocity.'
    }
  }
};

// --- DATASETS FOR AR6 FIGURES ---

export interface SPM1DataPoint {
  year: number;
  observedTemp: number | null;
  humanForcing: number;
  naturalForcing: number;
  paleoLow?: number;
  paleoHigh?: number;
}

export const SPM1_DATA: SPM1DataPoint[] = [
  { year: 1, observedTemp: -0.1, humanForcing: 0.0, naturalForcing: -0.1 },
  { year: 500, observedTemp: -0.05, humanForcing: 0.0, naturalForcing: -0.05 },
  { year: 1000, observedTemp: 0.05, humanForcing: 0.0, naturalForcing: 0.05 },
  { year: 1400, observedTemp: -0.15, humanForcing: 0.0, naturalForcing: -0.15 },
  { year: 1700, observedTemp: -0.2, humanForcing: 0.0, naturalForcing: -0.2 },
  { year: 1850, observedTemp: -0.1, humanForcing: 0.02, naturalForcing: -0.08 },
  { year: 1870, observedTemp: -0.12, humanForcing: 0.04, naturalForcing: -0.10 },
  { year: 1890, observedTemp: -0.20, humanForcing: 0.08, naturalForcing: -0.15 },
  { year: 1910, observedTemp: -0.32, humanForcing: 0.12, naturalForcing: -0.18 },
  { year: 1930, observedTemp: -0.14, humanForcing: 0.22, naturalForcing: -0.05 },
  { year: 1950, observedTemp: -0.05, humanForcing: 0.35, naturalForcing: -0.08 },
  { year: 1970, observedTemp: 0.08, humanForcedGMST: 0.52, naturalForcing: -0.05 } as any,
  { year: 1990, observedTemp: 0.42, humanForcing: 0.78, naturalForcing: -0.02 },
  { year: 2000, observedTemp: 0.61, humanForcing: 0.95, naturalForcing: 0.01 },
  { year: 2010, observedTemp: 0.88, humanForcing: 1.15, naturalForcing: -0.01 },
  { year: 2020, observedTemp: 1.19, humanForcing: 1.28, naturalForcing: 0.02 }
];

export interface SPM2Driver {
  id: string;
  name: string;
  category: 'greenhouse' | 'aerosol' | 'land_solar';
  forcingWn2: number;
  tempImpact: number;
  color: string;
}

export const SPM2_DRIVERS: SPM2Driver[] = [
  { id: 'co2', name: 'Carbon Dioxide (CO2)', category: 'greenhouse', forcingWn2: 2.16, tempImpact: 0.8, color: '#f43f5e' },
  { id: 'ch4', name: 'Methane (CH4)', category: 'greenhouse', forcingWn2: 0.54, tempImpact: 0.3, color: '#fb923c' },
  { id: 'n2o', name: 'Nitrous Oxide (N2O)', category: 'greenhouse', forcingWn2: 0.21, tempImpact: 0.1, color: '#facc15' },
  { id: 'halogen', name: 'Halogenated Gases', category: 'greenhouse', forcingWn2: 0.41, tempImpact: 0.15, color: '#c084fc' },
  { id: 'nox_voc', name: 'Ozone Precursors (NOx, VOC)', category: 'greenhouse', forcingWn2: 0.27, tempImpact: 0.08, color: '#f472b6' },
  { id: 'so2', name: 'Sulfur Dioxide (SO2 Aerosols)', category: 'aerosol', forcingWn2: -0.90, tempImpact: -0.35, color: '#22d3ee' },
  { id: 'oc', name: 'Organic Carbon Aerosols', category: 'aerosol', forcingWn2: -0.22, tempImpact: -0.08, color: '#38bdf8' },
  { id: 'bc', name: 'Black Carbon Aerosols', category: 'aerosol', forcingWn2: 0.15, tempImpact: 0.05, color: '#cbd5e1' },
  { id: 'land_use', name: 'Land Use & Albedo', category: 'land_solar', forcingWn2: -0.15, tempImpact: -0.06, color: '#34d399' },
  { id: 'solar_volc', name: 'Solar & Volcanic (Natural)', category: 'land_solar', forcingWn2: 0.02, tempImpact: 0.01, color: '#a78bfa' }
];

export interface SPM3Region {
  id: string;
  code: string;
  name: string;
  x: number;
  y: number;
  hotIncrease: 'high' | 'medium' | 'low' | 'none';
  precipIncrease: 'high' | 'medium' | 'low' | 'none';
  droughtIncrease: 'high' | 'medium' | 'low' | 'none';
}

export const SPM3_REGIONS: SPM3Region[] = [
  { id: 'nw_na', code: 'NWN', name: 'N.W. North America', x: 2, y: 1, hotIncrease: 'high', precipIncrease: 'medium', droughtIncrease: 'medium' },
  { id: 'ne_na', code: 'NEN', name: 'N.E. North America', x: 4, y: 1, hotIncrease: 'high', precipIncrease: 'high', droughtIncrease: 'low' },
  { id: 'w_eu', code: 'WEU', name: 'W. & C. Europe', x: 7, y: 2, hotIncrease: 'high', precipIncrease: 'high', droughtIncrease: 'high' },
  { id: 'e_asia', code: 'EAS', name: 'East Asia', x: 10, y: 2, hotIncrease: 'high', precipIncrease: 'high', droughtIncrease: 'medium' },
  { id: 'w_af', code: 'WAF', name: 'West Africa', x: 6, y: 4, hotIncrease: 'high', precipIncrease: 'medium', droughtIncrease: 'high' },
  { id: 's_am', code: 'SAM', name: 'S.E. South America', x: 4, y: 5, hotIncrease: 'high', precipIncrease: 'high', droughtIncrease: 'low' },
  { id: 'aus', code: 'EAU', name: 'Eastern Australia', x: 11, y: 5, hotIncrease: 'high', precipIncrease: 'low', droughtIncrease: 'high' }
];

export interface SPM4Scenario {
  id: string;
  name: string;
  color: string;
  trajectory: { year: number; temp: number; seaIce: number; pH: number }[];
}

export const SPM4_SCENARIOS: SPM4Scenario[] = [
  {
    id: 'ssp1_19',
    name: 'SSP1-1.9 (Very Low Emissions)',
    color: '#38bdf8',
    trajectory: [
      { year: 2015, temp: 1.1, seaIce: 4.3, pH: 8.05 },
      { year: 2030, temp: 1.4, seaIce: 3.2, pH: 8.03 },
      { year: 2050, temp: 1.6, seaIce: 2.8, pH: 8.01 },
      { year: 2075, temp: 1.5, seaIce: 3.1, pH: 8.02 },
      { year: 2100, temp: 1.4, seaIce: 3.4, pH: 8.04 }
    ]
  },
  {
    id: 'ssp1_26',
    name: 'SSP1-2.6 (Low Emissions)',
    color: '#60a5fa',
    trajectory: [
      { year: 2015, temp: 1.1, seaIce: 4.3, pH: 8.05 },
      { year: 2030, temp: 1.5, seaIce: 2.9, pH: 8.02 },
      { year: 2050, temp: 1.7, seaIce: 2.1, pH: 7.99 },
      { year: 2075, temp: 1.8, seaIce: 2.0, pH: 7.98 },
      { year: 2100, temp: 1.8, seaIce: 2.2, pH: 7.98 }
    ]
  },
  {
    id: 'ssp2_45',
    name: 'SSP2-4.5 (Intermediate)',
    color: '#fbbf24',
    trajectory: [
      { year: 2015, temp: 1.1, seaIce: 4.3, pH: 8.05 },
      { year: 2030, temp: 1.5, seaIce: 2.6, pH: 8.01 },
      { year: 2050, temp: 2.0, seaIce: 1.2, pH: 7.95 },
      { year: 2075, temp: 2.4, seaIce: 0.8, pH: 7.91 },
      { year: 2100, temp: 2.7, seaIce: 0.5, pH: 7.88 }
    ]
  },
  {
    id: 'ssp3_70',
    name: 'SSP3-7.0 (High Emissions)',
    color: '#fb923c',
    trajectory: [
      { year: 2015, temp: 1.1, seaIce: 4.3, pH: 8.05 },
      { year: 2030, temp: 1.6, seaIce: 2.3, pH: 7.99 },
      { year: 2050, temp: 2.3, seaIce: 0.6, pH: 7.91 },
      { year: 2075, temp: 3.1, seaIce: 0.2, pH: 7.82 },
      { year: 2100, temp: 3.6, seaIce: 0.05, pH: 7.77 }
    ]
  },
  {
    id: 'ssp5_85',
    name: 'SSP5-8.5 (Very High Emissions)',
    color: '#f43f5e',
    trajectory: [
      { year: 2015, temp: 1.1, seaIce: 4.3, pH: 8.05 },
      { year: 2030, temp: 1.6, seaIce: 2.1, pH: 7.98 },
      { year: 2050, temp: 2.4, seaIce: 0.4, pH: 7.88 },
      { year: 2075, temp: 3.6, seaIce: 0.05, pH: 7.76 },
      { year: 2100, temp: 4.4, seaIce: 0.0, pH: 7.68 }
    ]
  }
];

export interface SPM5LatitudeData {
  latitude: number;
  name: string;
  temp1_5: number;
  temp2_0: number;
  temp4_0: number;
}

export const SPM5_LATITUDE_DATA: SPM5LatitudeData[] = [
  { latitude: -80, name: 'Antarctica (-80°S)', temp1_5: 1.8, temp2_0: 2.5, temp4_0: 5.2 },
  { latitude: -60, name: 'Southern Ocean (-60°S)', temp1_5: 0.9, temp2_0: 1.3, temp4_0: 2.6 },
  { latitude: -40, name: 'Southern Mid-Lat (-40°S)', temp1_5: 1.1, temp2_0: 1.5, temp4_0: 3.1 },
  { latitude: -20, name: 'Southern Tropics (-20°S)', temp1_5: 1.3, temp2_0: 1.8, temp4_0: 3.7 },
  { latitude: 0, name: 'Equator (0°)', temp1_5: 1.4, temp2_0: 1.9, temp4_0: 3.9 },
  { latitude: 20, name: 'Northern Tropics (+20°N)', temp1_5: 1.5, temp2_0: 2.1, temp4_0: 4.3 },
  { latitude: 40, name: 'Northern Mid-Lat (+40°N)', temp1_5: 1.8, temp2_0: 2.6, temp4_0: 5.4 },
  { latitude: 60, name: 'Sub-Arctic (+60°N)', temp1_5: 2.4, temp2_0: 3.4, temp4_0: 7.1 },
  { latitude: 80, name: 'High Arctic (+80°N)', temp1_5: 3.2, temp2_0: 4.6, temp4_0: 9.8 }
];

export interface SPM6DataPoint {
  cumulativeCO2: number;
  yearApprox: number;
  tempCentral: number;
  tempLower: number;
  tempUpper: number;
}

export const SPM6_DATA: SPM6DataPoint[] = [
  { cumulativeCO2: 0, yearApprox: 1850, tempCentral: 0.0, tempLower: 0.0, tempUpper: 0.0 },
  { cumulativeCO2: 500, yearApprox: 1930, tempCentral: 0.28, tempLower: 0.17, tempUpper: 0.39 },
  { cumulativeCO2: 1000, yearApprox: 1970, tempCentral: 0.55, tempLower: 0.34, tempUpper: 0.77 },
  { cumulativeCO2: 1500, yearApprox: 1995, tempCentral: 0.83, tempLower: 0.51, tempUpper: 1.15 },
  { cumulativeCO2: 2000, yearApprox: 2010, tempCentral: 1.10, tempLower: 0.68, tempUpper: 1.54 },
  { cumulativeCO2: 2500, yearApprox: 2020, tempCentral: 1.38, tempLower: 0.85, tempUpper: 1.93 },
  { cumulativeCO2: 3000, yearApprox: 2035, tempCentral: 1.65, tempLower: 1.02, tempUpper: 2.31 },
  { cumulativeCO2: 3500, yearApprox: 2050, tempCentral: 1.93, tempLower: 1.19, tempUpper: 2.70 },
  { cumulativeCO2: 4000, yearApprox: 2065, tempCentral: 2.20, tempLower: 1.36, tempUpper: 3.08 },
  { cumulativeCO2: 4500, yearApprox: 2080, tempCentral: 2.48, tempLower: 1.53, tempUpper: 3.47 }
];

export interface SPM7ExtremeEvent {
  type: string;
  name: string;
  preIndustrial: number;
  temp1_5: number;
  temp2_0: number;
  temp4_0: number;
  unit: string;
}

export const SPM7_EXTREMES: SPM7ExtremeEvent[] = [
  { type: 'heat_10yr', name: '10-Year Extreme Heat Event', preIndustrial: 1.0, temp1_5: 4.1, temp2_0: 5.6, temp4_0: 9.4, unit: 'times per decade' },
  { type: 'heat_50yr', name: '50-Year Extreme Heat Event', preIndustrial: 1.0, temp1_5: 8.6, temp2_0: 13.9, temp4_0: 39.2, unit: 'times per 50 yrs' },
  { type: 'precip_10yr', name: 'Heavy Precipitation Event', preIndustrial: 1.0, temp1_5: 1.5, temp2_0: 1.7, temp4_0: 2.7, unit: 'times per decade' },
  { type: 'drought_10yr', name: 'Agricultural & Ecological Drought', preIndustrial: 1.0, temp1_5: 2.0, temp2_0: 2.4, temp4_0: 4.1, unit: 'times per decade' }
];

export interface SPM8SeaLevelData {
  year: number;
  ssp1_26: number;
  ssp2_45: number;
  ssp5_85: number;
  ssp5_highImpact: number;
}

export const SPM8_SEA_LEVEL: SPM8SeaLevelData[] = [
  { year: 2000, ssp1_26: 0.0, ssp2_45: 0.0, ssp5_85: 0.0, ssp5_highImpact: 0.0 },
  { year: 2050, ssp1_26: 0.2, ssp2_45: 0.23, ssp5_85: 0.30, ssp5_highImpact: 0.45 },
  { year: 2100, ssp1_26: 0.44, ssp2_45: 0.56, ssp5_85: 0.77, ssp5_highImpact: 1.60 },
  { year: 2150, ssp1_26: 0.62, ssp2_45: 0.85, ssp5_85: 1.32, ssp5_highImpact: 5.20 },
  { year: 2300, ssp1_26: 1.10, ssp2_45: 2.10, ssp5_85: 4.50, ssp5_highImpact: 15.00 }
];

export interface SPM9CIDCategory {
  category: string;
  drivers: { name: string; icon: string; soundType: string; globalTrend: string }[];
}

export const SPM9_CID_CATEGORIES: SPM9CIDCategory[] = [
  {
    category: 'Heat & Cold',
    drivers: [
      { name: 'Mean Surface Temp', icon: 'thermometer', soundType: 'warm_drone', globalTrend: 'Increase worldwide' },
      { name: 'Extreme Heat', icon: 'sun', soundType: 'heat_burst', globalTrend: 'Increase worldwide' },
      { name: 'Cold Spells / Frost', icon: 'snowflake', soundType: 'crisp_hiss', globalTrend: 'Decrease worldwide' }
    ]
  },
  {
    category: 'Wet & Dry',
    drivers: [
      { name: 'Heavy Precipitation', icon: 'cloud-rain', soundType: 'rain_rumble', globalTrend: 'Increase in most regions' },
      { name: 'Flood & River Discharge', icon: 'waves', soundType: 'river_surge', globalTrend: 'Increase in mid-to-high lat' },
      { name: 'Agricultural Drought', icon: 'sun-dim', soundType: 'dry_crinkle', globalTrend: 'Increase in Mediterranean/subtropics' }
    ]
  },
  {
    category: 'Coastal & Open Ocean',
    drivers: [
      { name: 'Relative Sea Level', icon: 'anchor', soundType: 'sub_ocean', globalTrend: 'Increase along 95% of coasts' },
      { name: 'Coastal Flood & Erosion', icon: 'shield-alert', soundType: 'wave_crash', globalTrend: 'Increase along almost all coasts' },
      { name: 'Ocean Acidification', icon: 'test-tube', soundType: 'bit_crush', globalTrend: 'Increase globally' }
    ]
  },
  {
    category: 'Snow & Ice',
    drivers: [
      { name: 'Arctic Sea Ice Area', icon: 'ice-cube', soundType: 'cryo_decay', globalTrend: 'Decrease rapidly' },
      { name: 'Permafrost Thaw', icon: 'mountain', soundType: 'thaw_sub', globalTrend: 'Widespread thaw' }
    ]
  }
];

export const SPM10_BUDGET_STATS = {
  historicalEmissions: 2560,
  budget1_5C: 500,
  budget2_0C: 1150,
  annualEmissions2021: 40
};

